import { b2Vec2 } from "./box2d-compat.js";
import { FlashLevelBaseRuntime } from "./flash-level-base-runtime.js";

const GOAL_SYMBOL = Object.freeze({
  imageWidth: 25.2,
  imageHeight: 46.5,
  imageOffsetX: -1.0,
  imageOffsetY: -15.2,
});

const CHECKPOINT_SYMBOL = Object.freeze({
  imageWidth: 10.8,
  imageHeight: 21.1,
  imageOffsetX: -0.5,
  imageOffsetY: -0.5,
});

const LEVEL11_DYNAMIC_SYMBOLS = Object.freeze({
  wagon: {
    imagePath: "port/generated/symbols/32/png/1.png",
    width: 74.55,
    height: 27.1,
    offsetX: -74.65,
    offsetY: -21.0,
  },
  wagonWindowed: {
    imagePath: "port/generated/symbols/37/png/1.png",
    width: 74.55,
    height: 27.1,
    offsetX: -74.65,
    offsetY: -21.0,
  },
  wheel: {
    imagePath: "port/generated/symbols/34/png/1.png",
    width: 8.95,
    height: 9.0,
    offsetX: -0.45,
    offsetY: -0.45,
  },
  train: {
    imagePath: "port/generated/symbols/39/png/1.png",
    width: 62.3,
    height: 28.4,
    offsetX: -0.05,
    offsetY: -21.55,
  },
  killRotate: {
    imagePath: "port/generated/symbols/60/png/1.png",
    width: 11.0,
    height: 81.0,
    offsetX: -5.5,
    offsetY: -40.5,
  },
  killStar: {
    imagePath: "port/generated/symbols/63/png/1.png",
    width: 41.0,
    height: 41.0,
    offsetX: -0.5,
    offsetY: -0.5,
  },
  killBrevno: {
    imagePath: "port/generated/symbols/70/png/1.png",
    width: 53.75,
    height: 4.0,
    offsetX: -0.5,
    offsetY: -0.5,
  },
});

const LEVEL11_ROLLING_STOCK_ORDER = Object.freeze([
  "vagon11",
  "koleso_11_2",
  "koleso_11_1",
  "vagon10",
  "koleso_10_2",
  "koleso_10_1",
  "vagon9",
  "koleso_9_2",
  "koleso_9_1",
  "vagon8",
  "koleso_8_2",
  "koleso_8_1",
  "vagon7",
  "koleso_7_2",
  "koleso_7_1",
  "vagon6",
  "koleso_6_2",
  "koleso_6_1",
  "vagon5",
  "koleso_5_2",
  "koleso_5_1",
  "vagon4",
  "koleso_4_2",
  "koleso_4_1",
  "vagon3",
  "koleso_3_2",
  "koleso_3_1",
  "vagon2",
  "koleso_2_2",
  "koleso_2_1",
  "vagon1",
  "koleso_1_2",
  "koleso_1_1",
  "kolesoTrain2",
  "kolesoTrain1",
  "train",
]);

const LEVEL11_HAZARD_ORDER = Object.freeze([
  "killRotate",
  "killStar1",
  "killStar2",
  "killStar0",
  "killBrevno0",
  "killBrevno1",
  "killBrevno2",
  "killBrevno3",
]);

const LEVEL11_WINDOWED_WAGONS = new Set(["vagon3", "vagon4", "vagon5", "vagon9"]);

function drawSymbolImage(ctx, image, symbol, x, y, rotationDeg = 0) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((rotationDeg * Math.PI) / 180);
  ctx.drawImage(image, symbol.offsetX, symbol.offsetY, symbol.width, symbol.height);
  ctx.restore();
}

function drawAnimatedFrame(ctx, image, symbol, x, y, rotationDeg = 0) {
  if (!image?.complete || !symbol) {
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  if (rotationDeg) {
    ctx.rotate((rotationDeg * Math.PI) / 180);
  }
  ctx.drawImage(image, symbol.imageOffsetX, symbol.imageOffsetY, symbol.imageWidth, symbol.imageHeight);
  ctx.restore();
}

export class LevelElevenRuntime extends FlashLevelBaseRuntime {
  buildLevel() {
    if (this.assetsEnabled) {
      this.stageImage.src = "port/generated/levels_fg/level_11/1.visual.runtime.svg";
      this.level11DynamicImages = {
        wagon: new Image(),
        wagonWindowed: new Image(),
        wheel: new Image(),
        train: new Image(),
        killRotate: new Image(),
        killStar: new Image(),
        killBrevno: new Image(),
      };
      for (const [key, symbol] of Object.entries(LEVEL11_DYNAMIC_SYMBOLS)) {
        this.level11DynamicImages[key].src = symbol.imagePath;
      }
    } else {
      this.level11DynamicImages = {};
    }

    const flashRollingStockRotationDeg = 0.173980712890625;
    for (const [name, layout] of Object.entries(this.levelData.layout.namedChildren ?? {})) {
      if (
        name === "train"
        || /^vagon\d+$/.test(name)
        || /^kolesoTrain[12]$/.test(name)
        || /^koleso_\d+_[12]$/.test(name)
      ) {
        layout.rotation = flashRollingStockRotationDeg;
      }
    }

    const creationOrder = [
      "levelAim",
      "train",
      "kolesoTrain1",
      "kolesoTrain2",
      "vagon1", "koleso_1_1", "koleso_1_2",
      "vagon2", "koleso_2_1", "koleso_2_2",
      "vagon3", "koleso_3_1", "koleso_3_2",
      "vagon4", "koleso_4_1", "koleso_4_2",
      "vagon5", "koleso_5_1", "koleso_5_2",
      "vagon6", "koleso_6_1", "koleso_6_2",
      "vagon7", "koleso_7_1", "koleso_7_2",
      "vagon8", "koleso_8_1", "koleso_8_2",
      "vagon9", "koleso_9_1", "koleso_9_2",
      "vagon10", "koleso_10_1", "koleso_10_2",
      "vagon11", "koleso_11_1", "koleso_11_2",
      "platform",
      "ceil1",
      "killCeil1",
      "ceil2_1",
      "ceil2_2",
      "killRotate",
      "killStar0",
      "killStar1",
      "killStar2",
      "killBrevno0",
      "killBrevno1",
      "killBrevno2",
      "killBrevno3",
      "triangle",
      "upCeil",
    ];
    const created = new Set();
    for (const name of creationOrder) {
      this.createBodyFromLevelData(name);
      created.add(name);
    }
    for (const entry of this.levelData.bodies) {
      if (!created.has(entry.name)) {
        this.createBodyFromLevelData(entry.name);
      }
    }
    this.killStarBodies = [this.getBody("killStar0"), this.getBody("killStar1"), this.getBody("killStar2")].filter(Boolean);
    this.killBrevnoBodies = [this.getBody("killBrevno0"), this.getBody("killBrevno1"), this.getBody("killBrevno2"), this.getBody("killBrevno3")].filter(Boolean);
    this.killRotateBody = this.getBody("killRotate");
    this.killCeil1Body = this.getBody("killCeil1");
    this.trainBody = this.getBody("train");
    this.kolesoTrain1Body = this.getBody("kolesoTrain1");
    this.kolesoTrain2Body = this.getBody("kolesoTrain2");

    this.addRevoluteJoint(this.killRotateBody, this.ground, this.killRotateBody.GetPosition(), { motorSpeed: -Math.PI, maxMotorTorque: 5000, enableMotor: true });
    for (const body of this.killStarBodies) {
      this.addRevoluteJoint(body, this.ground, new b2Vec2(body.GetPosition().x + 20 / 30, body.GetPosition().y + 20 / 30), { motorSpeed: -5 * Math.PI, maxMotorTorque: 5000, enableMotor: true });
    }
    this.addRevoluteJoint(this.trainBody, this.kolesoTrain1Body, new b2Vec2(this.kolesoTrain1Body.GetPosition().x + 4 / 30, this.kolesoTrain1Body.GetPosition().y + 4 / 30), { motorSpeed: 7 * Math.PI, maxMotorTorque: 5000, enableMotor: true });
    this.addRevoluteJoint(this.trainBody, this.kolesoTrain2Body, new b2Vec2(this.kolesoTrain2Body.GetPosition().x + 4 / 30, this.kolesoTrain2Body.GetPosition().y + 4 / 30), { motorSpeed: 7 * Math.PI, maxMotorTorque: 5000, enableMotor: true });
    const freeWheelOptions = { motorSpeed: 7 * Math.PI, maxMotorTorque: 5000, enableMotor: false };
    for (let index = 1; index <= 11; index += 1) {
      const wagon = this.getBody(`vagon${index}`);
      const wheel1 = this.getBody(`koleso_${index}_1`);
      const wheel2 = this.getBody(`koleso_${index}_2`);
      this.addRevoluteJoint(wagon, wheel1, new b2Vec2(wheel1.GetPosition().x + 4 / 30, wheel1.GetPosition().y + 4 / 30), freeWheelOptions);
      this.addRevoluteJoint(wagon, wheel2, new b2Vec2(wheel2.GetPosition().x + 4 / 30, wheel2.GetPosition().y + 4 / 30), freeWheelOptions);
    }
    this.addDistanceJoint(this.trainBody, this.getBody("vagon1"), this.trainBody.GetPosition(), this.getBody("vagon1").GetPosition());
    for (let index = 1; index <= 10; index += 1) {
      const wagonA = this.getBody(`vagon${index}`);
      const wagonB = this.getBody(`vagon${index + 1}`);
      this.addDistanceJoint(wagonA, wagonB, new b2Vec2(wagonA.GetPosition().x - 75 / 30, wagonA.GetPosition().y), wagonB.GetPosition());
    }
  }

  levelUpdate() {
    const hazards = [this.killCeil1Body, this.killRotateBody, ...this.killStarBodies, ...this.killBrevnoBodies].filter(Boolean);
    const hitHazard = hazards.some((body) => this.contactListener.playerContactBodies.indexOf(body) !== -1);
    if (this.player.y > 500 || (this.spikesKill && hitHazard)) {
      if (this.player.isLive()) {
        this.startDeath();
      }
    }
  }

  getLevel11SymbolForBody(name) {
    if (name === "train") {
      return { image: this.level11DynamicImages.train, symbol: LEVEL11_DYNAMIC_SYMBOLS.train };
    }
    if (/^vagon\d+$/.test(name)) {
      const isWindowed = LEVEL11_WINDOWED_WAGONS.has(name);
      return isWindowed
        ? { image: this.level11DynamicImages.wagonWindowed, symbol: LEVEL11_DYNAMIC_SYMBOLS.wagonWindowed }
        : { image: this.level11DynamicImages.wagon, symbol: LEVEL11_DYNAMIC_SYMBOLS.wagon };
    }
    if (/^kolesoTrain[12]$/.test(name) || /^koleso_\d+_[12]$/.test(name)) {
      return { image: this.level11DynamicImages.wheel, symbol: LEVEL11_DYNAMIC_SYMBOLS.wheel };
    }
    if (name === "killRotate") {
      return { image: this.level11DynamicImages.killRotate, symbol: LEVEL11_DYNAMIC_SYMBOLS.killRotate };
    }
    if (/^killStar\d+$/.test(name)) {
      return { image: this.level11DynamicImages.killStar, symbol: LEVEL11_DYNAMIC_SYMBOLS.killStar };
    }
    if (/^killBrevno\d+$/.test(name)) {
      return { image: this.level11DynamicImages.killBrevno, symbol: LEVEL11_DYNAMIC_SYMBOLS.killBrevno };
    }
    return null;
  }

  drawLevel11Bodies(ctx, names) {
    for (const name of names) {
      const layout = this.getDisplayLayout(name);
      if (!layout) {
        continue;
      }
      const mapped = this.getLevel11SymbolForBody(name);
      if (!mapped) {
        continue;
      }
      drawSymbolImage(
        ctx,
        mapped.image,
        mapped.symbol,
        layout.x,
        layout.y,
        layout.rotation ?? 0,
      );
    }
  }

  render(ctx, stageWidth, stageHeight, options = {}) {
    const zoom = Math.min(4, Math.max(0.1, Number(options.zoom) || 1));
    ctx.save();
    ctx.clearRect(0, 0, stageWidth, stageHeight);
    ctx.translate(stageWidth / 2, stageHeight / 2);
    ctx.scale(zoom, zoom);
    ctx.translate(-stageWidth / 2, -stageHeight / 2);

    if (this.backgroundImage?.complete && this.backgroundStart && this.backgroundSymbol?.layout) {
      const sym = this.backgroundSymbol;
      const bx = this.backgroundStart.x - this.camera.x / 10;
      const by = this.backgroundStart.y - this.camera.y / 10;
      ctx.save();
      ctx.translate(bx, by);
      if (sym.pngLayout) {
        const scaleX = sym.layout.imageWidth / sym.pngLayout.sourceWidth;
        const scaleY = sym.layout.imageHeight / sym.pngLayout.sourceHeight;
        ctx.drawImage(
          this.backgroundImage,
          sym.pngLayout.cropX, sym.pngLayout.cropY,
          sym.pngLayout.cropWidth, sym.pngLayout.cropHeight,
          sym.layout.imageOffsetX + sym.pngLayout.cropX * scaleX,
          sym.layout.imageOffsetY + sym.pngLayout.cropY * scaleY,
          sym.pngLayout.cropWidth * scaleX,
          sym.pngLayout.cropHeight * scaleY,
        );
      } else {
        ctx.drawImage(this.backgroundImage, sym.layout.imageOffsetX, sym.layout.imageOffsetY);
      }
      ctx.restore();
    }

    ctx.translate(this.camera.x, this.camera.y);

    // In Flash timeline, rolling stock is behind the static foreground geometry.
    this.drawLevel11Bodies(ctx, LEVEL11_ROLLING_STOCK_ORDER);

    if (this.stageImage?.complete) {
      const { layout } = this.levelData;
      ctx.drawImage(this.stageImage, layout.imageOffsetX, layout.imageOffsetY, layout.imageWidth, layout.imageHeight);
    }

    this.drawLevel11Bodies(ctx, LEVEL11_HAZARD_ORDER);

    for (const checkpointState of this.checkpoints) {
      const checkpointFrame = this.checkpointFrames[checkpointState.currentFrame - 1];
      drawAnimatedFrame(ctx, checkpointFrame, CHECKPOINT_SYMBOL, checkpointState.x, checkpointState.y);
    }

    const goalLayout = this.getDisplayLayout("levelAim");
    const goalFrame = this.goalFrames[this.goalCurrentFrame - 1];
    if (goalLayout) {
      drawAnimatedFrame(ctx, goalFrame, GOAL_SYMBOL, goalLayout.x, goalLayout.y, goalLayout.rotation ?? 0);
    }

    if (this.player?.isLive?.()) {
      ctx.save();
      ctx.translate(this.player.x, this.player.y);
      ctx.rotate(this.player.body.GetAngle());
      if (this.playerImage?.complete) {
        ctx.drawImage(this.playerImage, -10.5, -10.5);
      }
      ctx.restore();
    }

    ctx.fillStyle = "#d32f2f";
    for (const particle of this.particles) {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
